import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { COUNTRIES } from "~/lib/countries";
import {
    type TenantDetailRecord,
    useUpdateTenant,
} from "~/lib/tenants";

interface EditOrganizationDialogProps {
    tenant: TenantDetailRecord;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

// OTHER_SENTINEL is the value our country <select> takes when the operator
// wants to type a country that isn't in the curated list. The text input
// below the select then drives the final value submitted to the API.
const OTHER_SENTINEL = "__other__";

export function EditOrganizationDialog({
    tenant,
    open,
    onOpenChange,
}: EditOrganizationDialogProps): React.ReactElement {
    const update = useUpdateTenant();

    const [legalName, setLegalName] = useState(tenant.legalName ?? "");
    const [industry, setIndustry] = useState(tenant.industry ?? "");
    const [countrySelect, setCountrySelect] = useState<string>(() => {
        if (!tenant.country) return "";
        return COUNTRIES.some((c) => c.code === tenant.country) ? tenant.country : OTHER_SENTINEL;
    });
    const [countryOther, setCountryOther] = useState(
        tenant.country && !COUNTRIES.some((c) => c.code === tenant.country) ? tenant.country : "",
    );
    const [taxId, setTaxId] = useState(tenant.taxId ?? "");
    const [website, setWebsite] = useState(tenant.website ?? "");
    const [addressLine1, setAddressLine1] = useState(tenant.addressLine1 ?? "");
    const [addressLine2, setAddressLine2] = useState(tenant.addressLine2 ?? "");
    const [city, setCity] = useState(tenant.city ?? "");
    const [state, setState] = useState(tenant.state ?? "");
    const [postalCode, setPostalCode] = useState(tenant.postalCode ?? "");
    const [notes, setNotes] = useState(tenant.notes ?? "");
    const [error, setError] = useState<string | null>(null);

    // Re-seed fields when the dialog is re-opened against a mutated tenant.
    useEffect(() => {
        if (!open) return;
        setLegalName(tenant.legalName ?? "");
        setIndustry(tenant.industry ?? "");
        setCountrySelect(
            !tenant.country
                ? ""
                : COUNTRIES.some((c) => c.code === tenant.country)
                    ? tenant.country
                    : OTHER_SENTINEL,
        );
        setCountryOther(
            tenant.country && !COUNTRIES.some((c) => c.code === tenant.country)
                ? tenant.country
                : "",
        );
        setTaxId(tenant.taxId ?? "");
        setWebsite(tenant.website ?? "");
        setAddressLine1(tenant.addressLine1 ?? "");
        setAddressLine2(tenant.addressLine2 ?? "");
        setCity(tenant.city ?? "");
        setState(tenant.state ?? "");
        setPostalCode(tenant.postalCode ?? "");
        setNotes(tenant.notes ?? "");
        setError(null);
        update.reset();
    }, [open, tenant]); // eslint-disable-line react-hooks/exhaustive-deps

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        let country = "";
        if (countrySelect === OTHER_SENTINEL) {
            country = countryOther.trim().toUpperCase();
            if (country && country.length !== 2) {
                setError("Country must be an ISO 3166-1 alpha-2 code (2 letters).");
                return;
            }
        } else {
            country = countrySelect;
        }
        try {
            await update.mutateAsync({
                id: tenant.id,
                legalName,
                industry,
                country,
                taxId,
                website,
                addressLine1,
                addressLine2,
                city,
                state,
                postalCode,
                notes,
            });
            toast.success("Organization updated");
            onOpenChange(false);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to save.");
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content
                        className="dialog-content enter-up"
                        style={{ padding: "var(--space-6)", maxWidth: 640 }}
                    >
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Edit organization</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <Dialog.Description asChild>
                            <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                                <div className="stack">
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: "var(--space-3)",
                                        }}
                                    >
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">Legal name</span>
                                            <input
                                                className="input"
                                                value={legalName}
                                                onChange={(e) => setLegalName(e.target.value)}
                                                placeholder="Acme Inc."
                                            />
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">Industry</span>
                                            <input
                                                className="input"
                                                value={industry}
                                                onChange={(e) => setIndustry(e.target.value)}
                                                placeholder="Finance"
                                            />
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">Country</span>
                                            <select
                                                className="input"
                                                value={countrySelect}
                                                onChange={(e) => setCountrySelect(e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {COUNTRIES.map((c) => (
                                                    <option key={c.code} value={c.code}>
                                                        {c.flag} {c.name} ({c.code})
                                                    </option>
                                                ))}
                                                <option value={OTHER_SENTINEL}>Other…</option>
                                            </select>
                                            {countrySelect === OTHER_SENTINEL && (
                                                <input
                                                    className="input"
                                                    value={countryOther}
                                                    onChange={(e) => setCountryOther(e.target.value)}
                                                    placeholder="alpha-2 code (e.g. CL)"
                                                    maxLength={2}
                                                    style={{
                                                        marginTop: "var(--space-1)",
                                                        textTransform: "uppercase",
                                                    }}
                                                />
                                            )}
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">Tax ID</span>
                                            <input
                                                className="input"
                                                value={taxId}
                                                onChange={(e) => setTaxId(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs" style={{ gridColumn: "1 / -1" }}>
                                            <span className="text-12 text-text-muted">Website</span>
                                            <input
                                                className="input"
                                                type="url"
                                                value={website}
                                                onChange={(e) => setWebsite(e.target.value)}
                                                placeholder="https://acme.example"
                                            />
                                        </label>
                                        <label className="stack-xs" style={{ gridColumn: "1 / -1" }}>
                                            <span className="text-12 text-text-muted">Address line 1</span>
                                            <input
                                                className="input"
                                                value={addressLine1}
                                                onChange={(e) => setAddressLine1(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs" style={{ gridColumn: "1 / -1" }}>
                                            <span className="text-12 text-text-muted">Address line 2</span>
                                            <input
                                                className="input"
                                                value={addressLine2}
                                                onChange={(e) => setAddressLine2(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">City</span>
                                            <input
                                                className="input"
                                                value={city}
                                                onChange={(e) => setCity(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">State / Region</span>
                                            <input
                                                className="input"
                                                value={state}
                                                onChange={(e) => setState(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs">
                                            <span className="text-12 text-text-muted">Postal code</span>
                                            <input
                                                className="input"
                                                value={postalCode}
                                                onChange={(e) => setPostalCode(e.target.value)}
                                            />
                                        </label>
                                        <label className="stack-xs" style={{ gridColumn: "1 / -1" }}>
                                            <span className="text-12 text-text-muted">Notes</span>
                                            <textarea
                                                className="input"
                                                rows={3}
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                            />
                                        </label>
                                    </div>
                                </div>

                                {error && (
                                    <div
                                        className="chip chip--danger shake"
                                        style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                        role="alert"
                                    >
                                        {error}
                                    </div>
                                )}

                                <div className="cluster-xs" style={{ justifyContent: "flex-end" }}>
                                    <Dialog.Close asChild>
                                        <button type="button" className="btn btn--ghost">Cancel</button>
                                    </Dialog.Close>
                                    <button type="submit" className="btn btn--primary press-crush" disabled={update.isPending}>
                                        {update.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                        <span>{update.isPending ? "Saving…" : "Save"}</span>
                                    </button>
                                </div>
                            </form>
                        </Dialog.Description>
                    </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
